/**
 * Performance Lab — client-side session state.
 * Nothing here writes to Supabase. Promotion happens only via explicit "Save to Club".
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

export type PlayerStatus = "fit" | "injured" | "suspended" | "unavailable";

export interface LabPlayer {
  id: string;
  first_name: string;
  last_name: string;
  position: string | null;
  status: PlayerStatus;
  rtp_stage?: string | null;
  is_hypothetical?: boolean;
}

export interface LabFixture {
  id: string;
  match_date: string;
  opponent: string;
  venue: string;
  competition?: string | null;
}

export interface LineupEntry {
  fixture_id: string;
  position_slot: string; // e.g. "GK", "CB1", "ST", "SUB1"
  player_id: string;
}

export interface LabSessionState {
  isOpen: boolean;
  isDirty: boolean; // any manipulation made
  sessionStart: Date | null;
  players: LabPlayer[];
  fixtures: LabFixture[];
  lineups: LineupEntry[]; // draft lineups per fixture
  sessionNote: string;
  activeScenario: number; // 0-based index, PI only
  scenarios: string[]; // scenario names for PI multi-scenario
}

interface PerformanceLabContextValue {
  session: LabSessionState;
  openLab: () => void;
  closeLab: () => void;
  loadSnapshot: (players: LabPlayer[], fixtures: LabFixture[]) => void;
  updatePlayerStatus: (playerId: string, status: PlayerStatus) => void;
  addHypotheticalPlayer: (position: string, label: string) => void;
  setLineupEntry: (fixtureId: string, positionSlot: string, playerId: string | null) => void;
  setSessionNote: (note: string) => void;
  discardAndExit: () => void;
  setActiveScenario: (idx: number) => void;
  addScenario: (name: string) => void;
}

const defaultSession: LabSessionState = {
  isOpen: false,
  isDirty: false,
  sessionStart: null,
  players: [],
  fixtures: [],
  lineups: [],
  sessionNote: "",
  activeScenario: 0,
  scenarios: ["Scenario 1"],
};

const PerformanceLabContext = createContext<PerformanceLabContextValue | null>(null);

export function PerformanceLabProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<LabSessionState>(defaultSession);

  const openLab = useCallback(() => {
    setSession((s) => ({
      ...s,
      isOpen: true,
      sessionStart: s.sessionStart ?? new Date(),
    }));
  }, []);

  const closeLab = useCallback(() => {
    setSession(defaultSession);
  }, []);

  const loadSnapshot = useCallback((players: LabPlayer[], fixtures: LabFixture[]) => {
    setSession((s) => ({ ...s, players, fixtures }));
  }, []);

  const updatePlayerStatus = useCallback((playerId: string, status: PlayerStatus) => {
    setSession((s) => ({
      ...s,
      isDirty: true,
      players: s.players.map((p) => (p.id === playerId ? { ...p, status } : p)),
    }));
  }, []);

  const addHypotheticalPlayer = useCallback((position: string, label: string) => {
    const hypo: LabPlayer = {
      id: `hypo-${Date.now()}`,
      first_name: label,
      last_name: "",
      position,
      status: "fit",
      is_hypothetical: true,
    };
    setSession((s) => ({
      ...s,
      isDirty: true,
      players: [...s.players, hypo],
    }));
  }, []);

  const setLineupEntry = useCallback((fixtureId: string, positionSlot: string, playerId: string | null) => {
    setSession((s) => {
      const filtered = s.lineups.filter(
        (l) => !(l.fixture_id === fixtureId && l.position_slot === positionSlot),
      );
      const next: LineupEntry[] = playerId
        ? [...filtered, { fixture_id: fixtureId, position_slot: positionSlot, player_id: playerId }]
        : filtered;
      return { ...s, isDirty: true, lineups: next };
    });
  }, []);

  const setSessionNote = useCallback((note: string) => {
    setSession((s) => ({ ...s, sessionNote: note }));
  }, []);

  const discardAndExit = useCallback(() => {
    setSession(defaultSession);
  }, []);

  const setActiveScenario = useCallback((idx: number) => {
    setSession((s) => ({ ...s, activeScenario: idx }));
  }, []);

  const addScenario = useCallback((name: string) => {
    setSession((s) => {
      if (s.scenarios.length >= 3) return s;
      return { ...s, scenarios: [...s.scenarios, name] };
    });
  }, []);

  // Browser beforeunload guard
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (session.isOpen && session.isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [session.isOpen, session.isDirty]);

  return (
    <PerformanceLabContext.Provider
      value={{
        session,
        openLab,
        closeLab,
        loadSnapshot,
        updatePlayerStatus,
        addHypotheticalPlayer,
        setLineupEntry,
        setSessionNote,
        discardAndExit,
        setActiveScenario,
        addScenario,
      }}
    >
      {children}
    </PerformanceLabContext.Provider>
  );
}

export function usePerformanceLab() {
  const ctx = useContext(PerformanceLabContext);
  if (!ctx) throw new Error("usePerformanceLab must be used within PerformanceLabProvider");
  return ctx;
}
